/**
 * Helpers for working out which exercises a learner is expected to have
 * finished before the one they are about to open.
 *
 * Exercises are ordered course-wide: modules by `order_index`, then the
 * exercises inside each module by their own `order_index`. Everything that
 * sits before the target in that order and is not complete is a pending
 * prerequisite.
 */

export interface ProgressLike {
  id: number
  title: string
  order_index?: number | null
  progress?: number | null
  status?: string | null
}

export interface OrderedExercise extends ProgressLike {
  moduleId: number
  moduleTitle?: string | null
}

export interface ModuleLike {
  id: number
  title: string
  order_index?: number | null
}

export interface PendingPrerequisite {
  id: number
  title: string
  moduleTitle?: string | null
  isCurrentModule: boolean
}

export const isExerciseComplete = (exercise: ProgressLike): boolean =>
  exercise.status === 'completed' || (exercise.progress ?? 0) >= 100

const byOrderIndex = <T extends { order_index?: number | null; id: number }>(a: T, b: T): number => {
  const ao = a.order_index ?? Number.MAX_SAFE_INTEGER
  const bo = b.order_index ?? Number.MAX_SAFE_INTEGER
  return ao === bo ? a.id - b.id : ao - bo
}

/**
 * Flattens a course into a single ordered list of exercises.
 * Modules with no loaded exercises simply contribute nothing.
 */
export const buildCourseExerciseOrder = (
  modules: ModuleLike[],
  exercisesByModule: Record<number, ProgressLike[]>
): OrderedExercise[] =>
  [...modules].sort(byOrderIndex).flatMap((module) =>
    [...(exercisesByModule[module.id] ?? [])].sort(byOrderIndex).map((exercise) => ({
      ...exercise,
      moduleId: module.id,
      moduleTitle: module.title
    }))
  )

/**
 * Every incomplete exercise that comes before `exerciseId` in the course order.
 * Returns an empty list when the exercise is unknown or already first.
 */
export const findPendingPrerequisites = (
  orderedExercises: OrderedExercise[],
  exerciseId: number,
  currentModuleId?: number
): PendingPrerequisite[] => {
  const targetIndex = orderedExercises.findIndex((ex) => ex.id === exerciseId)
  if (targetIndex <= 0) return []

  return orderedExercises
    .slice(0, targetIndex)
    .filter((ex) => !isExerciseComplete(ex))
    .map((ex) => ({
      id: ex.id,
      title: ex.title,
      moduleTitle: ex.moduleTitle,
      isCurrentModule: ex.moduleId === currentModuleId
    }))
}
